import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "react-bootstrap-icons";
import { assignPaymentProfileToCustomers } from "@api";
import { toast } from "@components/toast/Toast";
import { ProfilePaymentIcons } from "@components";
import { cn } from "@utils";

const PaymentProfileSelector = ({
  customerId,
  customerName,
  paymentProfileId,
  paymentProfiles,
  onChange,
}) => {
  const [selectedId, setSelectedId] = useState(paymentProfileId);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setSelectedId((prev) =>
      prev === paymentProfileId ? prev : paymentProfileId,
    );
  }, [paymentProfileId]);

  const updatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? 224;
    setPosition({
      top: rect.bottom + 4,
      left: rect.right - menuWidth,
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleScroll = () => setOpen(false);
    const handleResize = () => updatePosition();
    const handleClickOutside = (e) => {
      if (
        !buttonRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const selected = paymentProfiles.find((p) => p.id === selectedId);
  const isReadOnly = paymentProfiles.length <= 1;

  if (isReadOnly) {
    return (
      <span className="inline-flex max-w-full min-w-0 items-center gap-1.5 text-sm text-zinc-900">
        <span className="truncate">{selected?.name ?? "-"}</span>
        {selected && (
          <span className="shrink-0">
            <ProfilePaymentIcons profile={selected} size="sm" />
          </span>
        )}
      </span>
    );
  }

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleSelect = async (e, profile) => {
    e.stopPropagation();
    setOpen(false);
    if (profile.id === selectedId) return;
    const previousId = selectedId;
    setSelectedId(profile.id);
    setSaving(true);
    try {
      // Controller swallows errors and returns null on failure (legacy
      // contract). Treat null as a failure so we don't lie to the user.
      const result = await assignPaymentProfileToCustomers(profile.id, [
        customerId,
      ]);
      if (result == null) {
        setSelectedId(previousId);
        toast({
          description: `Failed to assign "${profile.name}" profile to ${customerName}.`,
        });
        return;
      }
      toast({
        description: `"${profile.name}" profile assigned to ${customerName}`,
      });
      if (onChange) onChange(profile.id);
    } catch (error) {
      setSelectedId(previousId);
      console.error("Failed to update payment profile:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        ref={buttonRef}
        role="button"
        onClick={handleToggle}
        className={cn(
          "inline-flex max-w-full items-center gap-1 rounded-full border px-3 py-1 text-sm font-semibold cursor-pointer select-none transition-colors",
          open
            ? "text-blue-800 bg-blue-50 border-blue-200"
            : "text-zinc-600 bg-white border-zinc-200 hover:bg-zinc-50 hover:text-primary",
        )}
      >
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span className="truncate">{selected?.name ?? "-"}</span>
          {selected && (
            <span className="shrink-0">
              <ProfilePaymentIcons profile={selected} size="sm" />
            </span>
          )}
        </span>
        <ChevronDown className="w-4 h-4 shrink-0" />
      </div>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              zIndex: 50,
            }}
            className="min-w-56 w-max whitespace-nowrap rounded-md bg-white shadow-lg ring-1 ring-black/5 p-1 max-h-72 overflow-y-auto"
          >
            {paymentProfiles.map((profile) => (
              <div
                key={profile.id}
                role="button"
                onClick={(e) => handleSelect(e, profile)}
                className={cn(
                  "flex items-center rounded px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900",
                  saving && "pointer-events-none opacity-60",
                )}
              >
                <span
                  className={`flex items-center gap-2 select-none font-medium ${
                    profile.id === selectedId
                      ? "text-primary-500"
                      : "text-zinc-900"
                  }`}
                >
                  <span>{profile.name}</span>
                  <ProfilePaymentIcons profile={profile} size="sm" />
                </span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default PaymentProfileSelector;
